import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';

import { FacetData } from 'ish-core/models/facet/facet.interface';
import { FilterData } from 'ish-core/models/filter/filter.interface';
import { getCurrentLocale, getICMStaticURL } from 'ish-core/store/core/configuration';
import { stringToFormParams } from 'ish-core/utils/url-form-params';

import { FilterNavigationData } from './filter-navigation.interface';
import { FilterNavigation } from './filter-navigation.model';

@Injectable({ providedIn: 'root' })
export class FilterNavigationMapper {
  private icmStaticURL: string;
  private currentLocale = '-';
  constructor(store: Store) {
    store.pipe(select(getICMStaticURL)).subscribe(url => (this.icmStaticURL = url));
    store.pipe(select(getCurrentLocale)).subscribe(locale => (this.currentLocale = locale || '-'));
  }

  fromData(data: FilterNavigationData): FilterNavigation {
    return {
      filter: data?.elements
        ? data.elements.map(filterData => ({
            id: filterData.id,
            name: filterData.name,
            displayType: filterData.displayType,
            limitCount: filterData.limitCount || -1,
            facets: this.mapFacetData(filterData),
            selectionType: filterData.selectionType || 'single',
          }))
        : [],
    };
  }

  /**
   * parse image content reference into static url
   */
  private parseFilterValue(filterEntry: FacetData): string {
    if (filterEntry.mappedType === 'image' && filterEntry.mappedValue) {
      const urlParts = filterEntry.mappedValue.split(':');
      return `url(${this.icmStaticURL}/${urlParts[0]}/${this.currentLocale || '-'}${urlParts[1]})`;
    }
    return filterEntry.mappedValue;
  }

  private mapFacetData(filterData: FilterData) {
    return filterData.filterEntries
      ? filterData.filterEntries.reduce((acc, facet) => {
          const uri = this.removeSpgidFromUri(facet.link.uri);
          const category = uri.includes('/categories/')
            ? [uri.split('/productfilters')[0].split('/categories/')[1]]
            : undefined;
          if (facet.name !== 'Show all') {
            acc.push({
              name: facet.name,
              count: facet.count,
              selected: facet.selected,
              displayName: facet.displayValue || undefined,
              searchParameter: {
                ...stringToFormParams(facet.link.uri.split('?')[1]?.replace(/\+/g, '%20') || ''),
                category,
              },
              level: facet.level || 0,
              mappedValue: this.parseFilterValue(facet),
              mappedType: facet.mappedType || undefined,
            });
          } else {
            console.warn(`Limiting filters is not supported. Set limit to -1 in the BackOffice (${filterData.name})`);
          }
          return acc;
        }, [])
      : [];
  }

  private removeSpgidFromUri(uri: string): string {
    if (!uri?.includes(';spgid')) {
      return uri;
    }

    const pgidStart = uri.indexOf(';spgid');
    const pgidEnd = uri.indexOf('/', pgidStart);

    return uri.replace(uri.substring(pgidStart, pgidEnd), '');
  }
}
